import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../axiosInstance';
import AppBarHeader from './AppBarHeader';
import { jwtDecode } from 'jwt-decode';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import CSVImportExport from './shared/CSVImportExport';

const ImportExport = () => {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  const [activeTab, setActiveTab] = useState(0);
  const [formatTab, setFormatTab] = useState(0); // 0 for JSON, 1 for CSV
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importOptions, setImportOptions] = useState({
    clearExisting: false,
    importCollections: true,
    importWishlists: true,
    collections: {
      games: true,
      consoles: true,
      legoSets: true,
      movies: featureFlags.enableMoviesFeature,
      music: featureFlags.enableMusicFeature,
      books: featureFlags.enableBooksFeature,
      boardGames: featureFlags.enableBoardGamesFeature
    },
    wishlists: {
      games: true,
      consoles: true,
      legoSets: true,
      movies: featureFlags.enableMoviesFeature,
      music: featureFlags.enableMusicFeature,
      books: featureFlags.enableBooksFeature,
      boardGames: featureFlags.enableBoardGamesFeature
    }
  });
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Get current user info
  const token = localStorage.getItem('token');
  const userDecoded = token ? jwtDecode(token) : null;
  const userId = userDecoded ? userDecoded.id : null;
  const isAdmin = userDecoded ? userDecoded.isAdmin : false;

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleFormatTabChange = (event, newValue) => {
    setFormatTab(newValue);
  };

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get('/import-export/export');
      setExportData(response.data);
      
      // Create a JSON file for download
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'intercollect-export.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'Export successful! Your data has been downloaded.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data. Please try again.');
      setSnackbar({
        open: true,
        message: 'Export failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get('/import-export/template');
      
      // Create a JSON file for download
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'intercollect-import-template.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'Template downloaded! Fill it with your data and import.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template. Please try again.');
      setSnackbar({
        open: true,
        message: 'Template download failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      
      // Read the file
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setImportData(data);
          
          // Show success message
          setSnackbar({
            open: true,
            message: 'File loaded successfully. Review the data and click Import to proceed.',
            severity: 'info'
          });
        } catch (err) {
          console.error('Error parsing JSON file:', err);
          setError('Invalid JSON file. Please upload a valid export file.');
          setImportData(null);
          setSnackbar({
            open: true,
            message: 'Invalid JSON file. Please upload a valid export file.',
            severity: 'error'
          });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleImportOptionChange = (section, key, value) => {
    if (section) {
      setImportOptions(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [key]: value
        }
      }));
    } else {
      setImportOptions(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleImport = async () => {
    if (!importData) {
      setError('No import data available. Please upload a file first.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setImportResults(null);
    
    try {
      const response = await axiosInstance.post('/import-export/import', {
        collections: importData.collections,
        wishlists: importData.wishlists,
        options: importOptions
      });
      
      setImportResults(response.data.results);
      setSnackbar({
        open: true,
        message: 'Import completed successfully!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error importing data:', err);
      setError('Failed to import data. Please try again.');
      setSnackbar({
        open: true,
        message: 'Import failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  const renderImportOptions = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Import Options
      </Typography>
      
      <FormControlLabel
        control={
          <Checkbox
            checked={importOptions.clearExisting}
            onChange={(e) => handleImportOptionChange(null, 'clearExisting', e.target.checked)}
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography>Clear existing data before import</Typography>
            <Tooltip title="Warning: This will delete your existing collections and wishlists before importing new data.">
              <InfoIcon color="warning" sx={{ ml: 1, fontSize: 18 }} />
            </Tooltip>
          </Box>
        }
      />
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle1" gutterBottom>
        Data Types
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={importOptions.importCollections}
                onChange={(e) => handleImportOptionChange(null, 'importCollections', e.target.checked)}
              />
            }
            label="Import Collections"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={importOptions.importWishlists}
                onChange={(e) => handleImportOptionChange(null, 'importWishlists', e.target.checked)}
              />
            }
            label="Import Wishlists"
          />
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 2 }} />
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Collection Types</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importOptions.collections.games}
                    onChange={(e) => handleImportOptionChange('collections', 'games', e.target.checked)}
                    disabled={!importOptions.importCollections}
                  />
                }
                label="Games"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importOptions.collections.consoles}
                    onChange={(e) => handleImportOptionChange('collections', 'consoles', e.target.checked)}
                    disabled={!importOptions.importCollections}
                  />
                }
                label="Consoles"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importOptions.collections.legoSets}
                    onChange={(e) => handleImportOptionChange('collections', 'legoSets', e.target.checked)}
                    disabled={!importOptions.importCollections}
                  />
                }
                label="LEGO Sets"
              />
            </Grid>
            {featureFlags.enableMoviesFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.collections.movies}
                      onChange={(e) => handleImportOptionChange('collections', 'movies', e.target.checked)}
                      disabled={!importOptions.importCollections}
                    />
                  }
                  label="Movies"
                />
              </Grid>
            )}
            {featureFlags.enableMusicFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.collections.music}
                      onChange={(e) => handleImportOptionChange('collections', 'music', e.target.checked)}
                      disabled={!importOptions.importCollections}
                    />
                  }
                  label="Music"
                />
              </Grid>
            )}
            {featureFlags.enableBooksFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.collections.books}
                      onChange={(e) => handleImportOptionChange('collections', 'books', e.target.checked)}
                      disabled={!importOptions.importCollections}
                    />
                  }
                  label="Books"
                />
              </Grid>
            )}
            {featureFlags.enableBoardGamesFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.collections.boardGames}
                      onChange={(e) => handleImportOptionChange('collections', 'boardGames', e.target.checked)}
                      disabled={!importOptions.importCollections}
                    />
                  }
                  label="Board Games"
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Wishlist Types</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importOptions.wishlists.games}
                    onChange={(e) => handleImportOptionChange('wishlists', 'games', e.target.checked)}
                    disabled={!importOptions.importWishlists}
                  />
                }
                label="Games"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importOptions.wishlists.consoles}
                    onChange={(e) => handleImportOptionChange('wishlists', 'consoles', e.target.checked)}
                    disabled={!importOptions.importWishlists}
                  />
                }
                label="Consoles"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importOptions.wishlists.legoSets}
                    onChange={(e) => handleImportOptionChange('wishlists', 'legoSets', e.target.checked)}
                    disabled={!importOptions.importWishlists}
                  />
                }
                label="LEGO Sets"
              />
            </Grid>
            {featureFlags.enableMoviesFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.wishlists.movies}
                      onChange={(e) => handleImportOptionChange('wishlists', 'movies', e.target.checked)}
                      disabled={!importOptions.importWishlists}
                    />
                  }
                  label="Movies"
                />
              </Grid>
            )}
            {featureFlags.enableMusicFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.wishlists.music}
                      onChange={(e) => handleImportOptionChange('wishlists', 'music', e.target.checked)}
                      disabled={!importOptions.importWishlists}
                    />
                  }
                  label="Music"
                />
              </Grid>
            )}
            {featureFlags.enableBooksFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.wishlists.books}
                      onChange={(e) => handleImportOptionChange('wishlists', 'books', e.target.checked)}
                      disabled={!importOptions.importWishlists}
                    />
                  }
                  label="Books"
                />
              </Grid>
            )}
            {featureFlags.enableBoardGamesFeature && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importOptions.wishlists.boardGames}
                      onChange={(e) => handleImportOptionChange('wishlists', 'boardGames', e.target.checked)}
                      disabled={!importOptions.importWishlists}
                    />
                  }
                  label="Board Games"
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleImport}
          disabled={loading || !importData}
        >
          {loading ? <CircularProgress size={24} /> : 'Import Data'}
        </Button>
      </Box>
    </Box>
  );

  const getTotalErrors = (section) => {
    if (!importResults) return 0;
    
    return Object.values(importResults[section]).reduce((total, item) => {
      return total + (item.errors ? item.errors.length : 0);
    }, 0);
  };

  const renderImportResults = () => {
    if (!importResults) return null;
    
    return (
      <Box sx={{ mt: 4 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Import Results</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="h6" gutterBottom>Collections</Typography>
            <Grid container spacing={2}>
              {Object.entries(importResults.collections).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                    <Typography>Imported: {value.imported}</Typography>
                    <Typography>Skipped: {value.skipped}</Typography>
                    <Typography>Errors: {value.errors.length}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Wishlists</Typography>
            <Grid container spacing={2}>
              {Object.entries(importResults.wishlists).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                    <Typography>Imported: {value.imported}</Typography>
                    <Typography>Skipped: {value.skipped}</Typography>
                    <Typography>Errors: {value.errors.length}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            {/* Show errors if any */}
            {(getTotalErrors('collections') > 0 || getTotalErrors('wishlists') > 0) && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" color="error">Errors</Typography>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Collection Errors ({getTotalErrors('collections')})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(importResults.collections).map(([key, value]) => (
                      value.errors.length > 0 && (
                        <Box key={key} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                          {value.errors.map((error, index) => (
                            <Alert severity="error" key={index} sx={{ mb: 1 }}>
                              <Typography variant="body2"><strong>Error:</strong> {error.error}</Typography>
                              <Typography variant="body2"><strong>Item:</strong></Typography>
                              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                                {JSON.stringify(error.item, null, 2)}
                              </pre>
                            </Alert>
                          ))}
                        </Box>
                      )
                    ))}
                  </AccordionDetails>
                </Accordion>
                
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Wishlist Errors ({getTotalErrors('wishlists')})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(importResults.wishlists).map(([key, value]) => (
                      value.errors.length > 0 && (
                        <Box key={key} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                          {value.errors.map((error, index) => (
                            <Alert severity="error" key={index} sx={{ mb: 1 }}>
                              <Typography variant="body2"><strong>Error:</strong> {error.error}</Typography>
                              <Typography variant="body2"><strong>Item:</strong></Typography>
                              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                                {JSON.stringify(error.item, null, 2)}
                              </pre>
                            </Alert>
                          ))}
                        </Box>
                      )
                    ))}
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const renderExportTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Export Your Collections and Wishlists
      </Typography>
      <Typography variant="body1" paragraph>
        This will export all your collections and wishlists to a JSON file that you can download and save for backup or transfer to another account.
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Export Data'}
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  const renderImportTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Import Collections and Wishlists
      </Typography>
      <Typography variant="body1" paragraph>
        Upload a JSON file exported from InterCollect to import collections and wishlists.
      </Typography>
      
      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        <AlertTitle>Minimum Required Fields</AlertTitle>
        <Typography variant="body2">
          You can download a template with the minimum required fields for each collection type:
        </Typography>
        <ul>
          <li><strong>Games:</strong> name, platform</li>
          <li><strong>Consoles:</strong> name</li>
          <li><strong>LEGO Sets:</strong> name</li>
          <li><strong>Movies:</strong> name, format</li>
          <li><strong>Music:</strong> name, artist, format</li>
        </ul>
        <Typography variant="body2">
          The system will attempt to look up additional data for items with minimal information.
        </Typography>
      </Alert>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          disabled={loading}
        >
          Download Template
        </Button>
        <Button
          variant="contained"
          color="primary"
          component="label"
          startIcon={<UploadFileIcon />}
          disabled={loading}
        >
          Select File
          <input
            type="file"
            accept=".json"
            hidden
            onChange={handleImportFileChange}
          />
        </Button>
      </Box>
      
      {importFile && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography>Selected file: {importFile.name}</Typography>
          <Typography>Size: {Math.round(importFile.size / 1024)} KB</Typography>
        </Alert>
      )}
      
      {importData && (
        <>
          <Alert severity="success" sx={{ mt: 3 }}>
            <AlertTitle>File Loaded Successfully</AlertTitle>
            <Typography>
              Collections: {Object.values(importData.collections).reduce((total, arr) => total + arr.length, 0)} items
            </Typography>
            <Typography>
              Wishlists: {Object.values(importData.wishlists).reduce((total, arr) => total + arr.length, 0)} items
            </Typography>
          </Alert>
          
          {renderImportOptions()}
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleImport}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Import Data'}
            </Button>
          </Box>
          
          {importResults && renderImportResults()}
        </>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  return (
    <Box>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />

      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 4 }}>
        <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate('/profile')}>
          ← Back to Profile
        </Button>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Export" />
          <Tab label="Import" />
        </Tabs>
        
        <Box sx={{ p: 2 }}>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={formatTab}
              onChange={handleFormatTabChange}
              centered
              indicatorColor="secondary"
              textColor="secondary"
            >
              <Tab label="JSON Format" />
              <Tab label="CSV Format" />
            </Tabs>
          </Paper>
          
          {formatTab === 0 ? (
            // JSON Format
            activeTab === 0 ? renderExportTab() : renderImportTab()
          ) : (
            // CSV Format
            <CSVImportExport onSnackbarMessage={setSnackbar} />
          )}
        </Box>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default ImportExport;
